// The assignee control in a request heading. Since #803 a request is worked by a SET of
// principals — several people, whole teams, no primary — so every row toggles membership. Each
// toggle closes the popup and restores its trigger, keeping the result explicit for keyboard users.
//
// Only admin rank may change it, and that is enforced by the server on every write. What this
// file decides is what a non-admin SEES: not an empty search that looks broken, but the set and
// a line saying who decides. There is no "assign to me" any more — that was the self-service
// entry #803 removes.
import React from "react";
import { Avatar } from "@/ds";
import { Icon } from "@/lib";
import type { Assignee, AssigneeRef, TeamRow, UserRow } from "@/api";
import { useT } from "../i18n";

/** Display label for a person: their name when they set one, otherwise the email. */
export function assigneeLabel(user: { name?: string | null; email: string }): string {
  return user.name?.trim() || user.email;
}

/** `user:<id>` / `team:<id>` — the key both halves of the picker compare on. */
export function principalKey(principal: { kind: string; id: string }): string {
  return `${principal.kind}:${principal.id}`;
}

/**
 * Candidates for the picker: everyone matching the query, with the acting user first so an admin
 * putting themselves on a request does not have to hunt for their own name. The rest keep a
 * stable alphabetical order. The acting user is pinned only when they match the query — a search
 * that excludes them should not show them.
 */
export function rankAssignees(
  users: readonly UserRow[],
  currentUserId: string | null | undefined,
  query: string,
): UserRow[] {
  const needle = query.trim().toLowerCase();
  const matches = users.filter((u) => {
    if (!needle) return true;
    return assigneeLabel(u).toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle);
  });
  return matches.slice().sort((a, b) => {
    if (currentUserId) {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
    }
    return assigneeLabel(a).localeCompare(assigneeLabel(b), undefined, { sensitivity: "base" });
  });
}

/**
 * Teams offered for NEW assignment: matching the query, archived ones left out.
 *
 * An archived team already on the request keeps working it — the server refuses only new
 * assignments — so it is rendered from the set itself, not from this list.
 */
export function rankTeams(teams: readonly TeamRow[], query: string): TeamRow[] {
  const needle = query.trim().toLowerCase();
  return teams
    .filter((team) => !team.archivedAt)
    .filter((team) => !needle || team.name.toLowerCase().includes(needle))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

export interface AssigneePickerProps {
  assignees: readonly Assignee[];
  currentUserId: string | null | undefined;
  /** Whether this session may change who is on the request (admin rank). */
  canAssign: boolean;
  loadOperators: () => Promise<UserRow[]>;
  loadTeams: () => Promise<TeamRow[]>;
  onAdd: (principal: AssigneeRef) => void;
  onRemove: (principal: AssigneeRef) => void;
  highlighted?: boolean;
  busy?: boolean;
  inline?: boolean;
  emptyLabel?: string;
  triggerContext?: string;
}

// `transitionend` owns normal removal. This fallback covers interrupted/disabled
// transitions without making a stale, invisible dialog permanent.
const PRESENCE_FALLBACK_MS = 160;

export function AssigneePicker({
  assignees,
  currentUserId,
  canAssign,
  loadOperators,
  loadTeams,
  onAdd,
  onRemove,
  highlighted = false,
  busy = false,
  inline = false,
  emptyLabel,
  triggerContext,
}: AssigneePickerProps) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const [present, setPresent] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [operators, setOperators] = React.useState<UserRow[] | null>(null);
  const [teams, setTeams] = React.useState<TeamRow[] | null>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [loadAttempt, setLoadAttempt] = React.useState(0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const restoreFocusRef = React.useRef(false);
  const panelId = React.useId();

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const finishClose = React.useCallback(() => {
    setPresent(false);
    if (!restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    window.requestAnimationFrame(() => {
      if (triggerRef.current?.isConnected) triggerRef.current.focus({ preventScroll: true });
    });
  }, []);

  const openPicker = React.useCallback(() => {
    clearCloseTimer();
    restoreFocusRef.current = false;
    setPresent(true);
    setOpen(true);
  }, [clearCloseTimer]);

  const closePicker = React.useCallback((restoreFocus = true) => {
    if (!present) return;
    clearCloseTimer();
    restoreFocusRef.current = restoreFocus;
    setQuery("");
    setOpen(false);
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      finishClose();
    }, PRESENCE_FALLBACK_MS);
  }, [clearCloseTimer, finishClose, present]);

  React.useEffect(() => clearCloseTimer, [clearCloseTimer]);

  React.useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) closePicker(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closePicker(true);
    };
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [closePicker, open]);

  React.useLayoutEffect(() => {
    if (panelRef.current) panelRef.current.inert = !open;
  }, [open, present]);

  React.useLayoutEffect(() => {
    if (!open) return;
    setQuery("");
    searchRef.current?.focus();
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    if (!canAssign || operators !== null) return;
    let cancelled = false;
    // People and teams are two lists behind one panel. A failure in either shows the same note:
    // the operator cannot tell them apart and does not need to.
    Promise.all([loadOperators(), loadTeams()])
      .then(([users, rows]) => {
        if (cancelled) return;
        setOperators(Array.isArray(users) ? users : []);
        setTeams(Array.isArray(rows) ? rows : []);
        setLoadFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setOperators([]);
        setTeams([]);
        setLoadFailed(true);
      });
    return () => { cancelled = true; };
    // `operators` is intentionally not a dependency: a retry explicitly increments the attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canAssign, loadAttempt]);

  const finishPresenceExit = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (open || event.target !== event.currentTarget || event.propertyName !== "opacity") return;
    clearCloseTimer();
    finishClose();
  };

  const retryLoad = () => {
    setLoadFailed(false);
    setOperators(null);
    setTeams(null);
    setLoadAttempt((attempt) => attempt + 1);
    window.requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }));
  };

  const onKeys = new Set(assignees.map(principalKey));
  const toggle = (principal: AssigneeRef) => {
    if (busy) return;
    if (onKeys.has(principalKey(principal))) onRemove(principal);
    else onAdd(principal);
    closePicker(true);
  };

  const rankedUsers = canAssign ? rankAssignees(operators ?? [], currentUserId, query) : [];
  const rankedTeams = canAssign ? rankTeams(teams ?? [], query) : [];
  // An archived team still on the request is offered for removal even though it is not offered
  // for assignment — removing it is the only way to clear it.
  const archivedOnRequest = assignees.filter(
    (principal) => principal.kind === "team" && principal.archived,
  );

  const names = assignees.map((principal) =>
    principal.kind === "team" ? principal.name : assigneeLabel(principal));
  const label = names.length
    ? (names.length === 1 ? names[0]! : t("ws.assignedCount", { name: names[0]!, n: names.length - 1 }))
    : emptyLabel ?? t("ws.notAssigned");

  return (
    <div className={`assignee-picker${inline ? " assignee-picker--inline" : ""}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={[
          "assignee assignee--btn",
          names.length ? "" : "subtle",
          highlighted ? "assignee--pickup" : "",
        ].filter(Boolean).join(" ")}
        data-pickup-required={highlighted || undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={triggerContext ? `${triggerContext}: ${label}` : undefined}
        title={names.length ? names.join(", ") : emptyLabel ?? t("ws.notAssigned")}
        onClick={() => { if (open) closePicker(); else openPicker(); }}
      >
        {names.length
          ? <Avatar name={names[0]!} size="sm" />
          : <Icon name="user" size={14} />}
        <span className="assignee__name">{label}</span>
        <Icon name="chevronDown" size={13} />
      </button>

      {present && (
        <div
          ref={panelRef}
          id={panelId}
          className="assignee-picker__panel"
          role="dialog"
          aria-label={t("ws.assignPickerAria")}
          aria-hidden={!open || undefined}
          aria-busy={busy || (canAssign && operators === null && !loadFailed) ? true : undefined}
          data-presence={open ? "open" : "closed"}
          onTransitionEnd={finishPresenceExit}
        >
          {canAssign && (
            <div className="assignee-picker__search ribbon-search">
              <Icon name="search" size={14} />
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder={t("ws.assignSearchPlaceholder")}
                aria-label={t("ws.assignSearchPlaceholder")}
                onChange={(event) => setQuery(event.currentTarget.value)}
              />
            </div>
          )}

          {canAssign && operators === null && (
            <p className="assignee-picker__note">{t("common.loading")}</p>
          )}
          {canAssign && loadFailed && (
            <div className="assignee-picker__note assignee-picker__retry" role="alert">
              <span>{t("ws.assignListFailed")}</span>
              <button type="button" onClick={retryLoad}>{t("ws.assignRetry")}</button>
            </div>
          )}
          {canAssign && operators !== null && !loadFailed
            && !rankedUsers.length && !rankedTeams.length && (
            <p className="assignee-picker__note">{t("ws.assignNoMatches")}</p>
          )}

          {rankedTeams.length > 0 && (
            <div className="assignee-picker__list" data-testid="assignee-picker-teams">
              <p className="assignee-picker__group">{t("ws.assignTeamsGroup")}</p>
              {rankedTeams.map((team) => {
                const isOn = onKeys.has(`team:${team.id}`);
                return (
                  <button
                    key={team.id}
                    type="button"
                    aria-pressed={isOn}
                    disabled={busy}
                    onClick={() => toggle({ kind: "team", id: team.id })}
                  >
                    <Icon name="users" size={15} />
                    <span className="assignee-picker__who">
                      <span className="assignee-picker__label">{team.name}</span>
                      <span className="assignee-picker__sub">
                        {t("ws.assignTeamMembers", { n: team.members.length })}
                      </span>
                    </span>
                    {isOn && <Icon name="check" size={15} />}
                  </button>
                );
              })}
            </div>
          )}

          {rankedUsers.length > 0 && (
            <div className="assignee-picker__list" data-testid="assignee-picker-people">
              <p className="assignee-picker__group">{t("ws.assignPeopleGroup")}</p>
              {rankedUsers.map((operator) => {
                const isSelf = operator.id === currentUserId;
                const isOn = onKeys.has(`user:${operator.id}`);
                return (
                  <button
                    key={operator.id}
                    type="button"
                    aria-pressed={isOn}
                    disabled={busy}
                    onClick={() => toggle({ kind: "user", id: operator.id })}
                  >
                    <Avatar name={assigneeLabel(operator)} size="sm" />
                    <span className="assignee-picker__who">
                      <span className="assignee-picker__label">
                        {assigneeLabel(operator)}
                        {isSelf && <em>{t("ws.assignSelfSuffix")}</em>}
                      </span>
                      <span className="assignee-picker__sub">{operator.email}</span>
                    </span>
                    {isOn && <Icon name="check" size={15} />}
                  </button>
                );
              })}
            </div>
          )}

          {canAssign && archivedOnRequest.length > 0 && (
            <>
              <div className="assignee-picker__sep" />
              <div className="assignee-picker__list" data-testid="assignee-picker-archived">
                <p className="assignee-picker__group">{t("ws.assignArchivedGroup")}</p>
                {archivedOnRequest.map((principal) => (
                  <button
                    key={principal.id}
                    type="button"
                    aria-pressed
                    disabled={busy}
                    onClick={() => toggle({ kind: "team", id: principal.id })}
                  >
                    <Icon name="users" size={15} />
                    <span className="assignee-picker__who">
                      <span className="assignee-picker__label">
                        {principal.kind === "team" ? principal.name : principal.id}
                      </span>
                      <span className="assignee-picker__sub">{t("ws.assignArchivedNote")}</span>
                    </span>
                    <Icon name="check" size={15} />
                  </button>
                ))}
              </div>
            </>
          )}

          {/* A session without admin rank cannot change who is on a request, and there is no
              self-service entry any more. Say who decides rather than showing dead controls. */}
          {!canAssign && (
            <div className="assignee-picker__list">
              {names.length > 0 && names.map((name, index) => (
                <p key={`${name}-${index}`} className="assignee-picker__note">{name}</p>
              ))}
              <p className="assignee-picker__note">{t("ws.assignRestricted")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
